import React, { useState, useCallback, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import JitsiJoinModal from './JitsiJoinModal';
import { getJitsiRoomName, getPreferredJoinURL } from '../utils/jitsiHelper';
import './AdminOnlineClassesView.css';
import './StudentDetailPages.css';
import { scopeBySchool, schoolMatches } from '../utils/schoolScope';

function SuperAdminOnlineClassesView({ schoolId: propSchoolId }) {
  const [saSchoolId, setSaSchoolId] = useState(propSchoolId || '');
  const [saSchools, setSaSchools] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);
  // Dropdown is authoritative: "All Schools" ('' ) = national view
  const schoolId = saSchoolId || null;
  const [onlineClasses, setOnlineClasses] = useState([]);
  const [classInfoById, setClassInfoById] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedClass, setSelectedClass] = useState(null);
  const [jitsiModal, setJitsiModal] = useState({ isOpen: false, roomName: '', displayName: '' });

  const loadOnlineClasses = useCallback(async () => {
    try {
      setLoading(true);
      setOnlineClasses([]);

      // Fetch all classes for the school (case-insensitive match)
      const classesSnapshot = await getDocs(collection(db, 'classes'));
      const scopedClasses = scopeBySchool(classesSnapshot.docs.map(d => ({ id: d.id, ...d.data() })), schoolId);
      const infoMap = {};
      scopedClasses.forEach(c => { infoMap[c.id] = { name: c.name, schoolId: c.schoolId }; });
      setClassInfoById(infoMap);
      const classIds = scopedClasses.map(d => d.id);

      if (classIds.length === 0) {
        setLoading(false);
        return;
      }

      // Fetch online classes for each class in the school
      const allOnlineClasses = [];
      for (const classId of classIds) {
        const onlineClassesRef = collection(db, 'classes', classId, 'onlineClasses');
        const snapshot = await getDocs(onlineClassesRef);
        const classes = snapshot.docs.map(doc => ({
          id: doc.id,
          classId: classId,
          ...doc.data(),
        }));
        allOnlineClasses.push(...classes);
      }

      // Sort by date (newest first)
      allOnlineClasses.sort((a, b) => new Date(b.date) - new Date(a.date));
      setOnlineClasses(allOnlineClasses);
    } catch (error) {
      console.error('Error loading online classes:', error);
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    loadOnlineClasses();
  }, [loadOnlineClasses]);

  const handleJoinClass = (onlineClass) => {
    const joinURL = getPreferredJoinURL(onlineClass);
    if (!joinURL) {
      alert('This class is not joinable at this time.');
      return;
    }

    if (!joinURL.includes('meet.jit.si')) {
      window.open(joinURL, '_blank');
      return;
    }

    const roomName = getJitsiRoomName(onlineClass.id);
    const displayName = 'Admin (Observer)';
    setJitsiModal({ isOpen: true, roomName, displayName });
  };

  const closeJitsiModal = () => {
    setJitsiModal({ isOpen: false, roomName: '', displayName: '' });
  };

  const canJoin = (onlineClass) => {
    const isOnline = onlineClass.classMode === 'Online' || onlineClass.classMode === 'Hybrid';
    const isActive = onlineClass.status === 'Active' || onlineClass.status === 'active';
    const now = new Date();
    const classDate = new Date(onlineClass.date);
    const isToday = classDate.toDateString() === now.toDateString();
    return isOnline && (isActive || isToday) && getPreferredJoinURL(onlineClass);
  };

  const filteredClasses = onlineClasses.filter(c => {
    if (statusFilter === 'all') return true;
    return c.status?.toLowerCase() === statusFilter.toLowerCase();
  });

  const activeCount = onlineClasses.filter(c => c.status === 'Active' || c.status === 'active').length;
  const scheduledCount = onlineClasses.filter(c => c.status === 'Scheduled' || c.status === 'scheduled').length;
  const completedCount = onlineClasses.filter(c => c.status === 'Completed' || c.status === 'completed').length;

  const schoolNameFor = (sid) => saSchools.find(s => schoolMatches(s.id, sid))?.name || '';
  const classNameFor = (cid) => classInfoById[cid]?.name || '';
  const schoolNameForClass = (cid) => schoolNameFor(classInfoById[cid]?.schoolId) || classInfoById[cid]?.schoolId || '';

  if (selectedClass) {
    const oc = selectedClass;
    console.log('🔥 SuperAdminOnlineClasses: Opened class detail', oc.id);
    return (
      <div className="detail-page">
        <button className="detail-back-btn" onClick={() => setSelectedClass(null)}>◄ Back</button>
        <div className="detail-hero">
          <div className="detail-hero-icon">{oc.classMode === 'Online' ? '📹' : oc.classMode === 'Hybrid' ? '🔄' : '🏫'}</div>
          <div>
            <h2 className="detail-page-title">
              {oc.subjectName || 'Online Class'}
              <span className={`detail-badge ${(oc.status || '').toLowerCase()}`}>{oc.status || 'Scheduled'}</span>
            </h2>
            <p className="detail-page-subtitle">{oc.topic || 'No topic'}</p>
          </div>
        </div>

        <div className="detail-card">
          <h4>Class Details</h4>
          <div className="detail-rows">
            <div className="detail-row"><span className="detail-label">School</span><span className="detail-value">{schoolNameForClass(oc.classId) || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Class</span><span className="detail-value">{classNameFor(oc.classId) || oc.classId || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Subject</span><span className="detail-value">{oc.subjectName || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Teacher</span><span className="detail-value">{oc.teacherName || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Date</span><span className="detail-value">{oc.date ? new Date(oc.date).toLocaleDateString() : 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Time</span><span className="detail-value">{oc.startTime} - {oc.endTime}</span></div>
            <div className="detail-row"><span className="detail-label">Mode</span><span className="detail-value">{oc.classMode || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Status</span><span className="detail-value">{oc.status || 'N/A'}</span></div>
          </div>
        </div>

        {oc.description && (
          <div className="detail-card">
            <h4>Description</h4>
            <p className="detail-note">{oc.description}</p>
          </div>
        )}

        {canJoin(oc) && (
          <div className="detail-actions">
            <button className="btn-primary" onClick={() => handleJoinClass(oc)}>Join (Observe)</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="admin-online-classes-view">
      <h2>Online Classes (School-Wide)</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>

      {loading ? (
        <div className="loading">Loading online classes...</div>
      ) : onlineClasses.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📹</div>
          <h3>No Online Classes</h3>
          <p>Online classes scheduled by teachers across the school will appear here.</p>
        </div>
      ) : (
        <div className="online-classes-container">
          <div className="summary-cards">
            <div className="summary-card active">
              <div className="summary-icon">🟢</div>
              <div className="summary-value">{activeCount}</div>
              <div className="summary-label">Active</div>
            </div>
            <div className="summary-card scheduled">
              <div className="summary-icon">📅</div>
              <div className="summary-value">{scheduledCount}</div>
              <div className="summary-label">Scheduled</div>
            </div>
            <div className="summary-card completed">
              <div className="summary-icon">✅</div>
              <div className="summary-value">{completedCount}</div>
              <div className="summary-label">Completed</div>
            </div>
          </div>

          <div className="filter-bar">
            <label>Filter by status:</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="scheduled">Scheduled</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="classes-list">
            {filteredClasses.map(onlineClass => (
              <div key={onlineClass.id} className="class-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedClass(onlineClass)}>
                <div className="class-header">
                  <span className={`class-mode ${onlineClass.classMode?.toLowerCase()}`}>
                    {onlineClass.classMode === 'Online' ? '📹' : onlineClass.classMode === 'Hybrid' ? '🔄' : '🏫'}
                  </span>
                  <span className={`class-status ${onlineClass.status?.toLowerCase()}`}>
                    {onlineClass.status}
                  </span>
                </div>
                <h3>{onlineClass.subjectName || 'Class'}</h3>
                <p className="topic">{onlineClass.topic || 'No topic'}</p>
                <div className="class-meta">
                  <span>👨‍🏫 {onlineClass.teacherName || 'Teacher'}</span>
                  <span>📅 {new Date(onlineClass.date).toLocaleDateString()}</span>
                  <span>⏰ {onlineClass.startTime} - {onlineClass.endTime}</span>
                </div>
                {schoolNameForClass(onlineClass.classId) && (
                  <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '4px 0 0' }}>🏫 {schoolNameForClass(onlineClass.classId)}{classNameFor(onlineClass.classId) ? ` · ${classNameFor(onlineClass.classId)}` : ''}</p>
                )}
                {canJoin(onlineClass) && (
                  <button className="join-button" onClick={(e) => { e.stopPropagation(); handleJoinClass(onlineClass); }}>
                    Join (Observe)
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <JitsiJoinModal
        isOpen={jitsiModal.isOpen}
        roomName={jitsiModal.roomName}
        displayName={jitsiModal.displayName}
        onClose={closeJitsiModal}
      />
    </div>
  );
}

export default SuperAdminOnlineClassesView;
